import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Send, User, Lock, RefreshCw, Sparkles, Leaf, ChevronRight, Stethoscope, Utensils, Flower2, Volume2, Mic, ArrowRight, Bookmark, CheckCircle2, Brain } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message, QueryUsage, RecommendationMetadata, AppView, SubscriptionStatus, FeatureContext, UserProfile } from '../types';
import { sendMessageWithRAG } from '../services/geminiService';
import { getCurrentUser, saveConsultation } from '../services/backendService';
import { MAX_PROMPT_LENGTH } from '../utils/constants';
import WisdomNugget from './WisdomNugget';
import { Logo } from './Logo';
import { playRawAudio } from '../utils/audio';
import DailyRituals from './DailyRituals';
import WelcomeTour from './WelcomeTour';
import { authenticatedFetch } from '../utils/apiClient';
import logger from "@/utils/logger";

interface ChatInterfaceProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  onTrialEnd: () => void;
  hasAccess: boolean;
  subscriptionStatus: SubscriptionStatus;
  initialMessage?: string;
  onMessageSent?: () => void;
  usage: QueryUsage;
  onUpgradeClick: () => void;
  isGuest: boolean;
  onShowAuth: () => void;
  onNavigateToFeature: (view: AppView, context: FeatureContext) => void;
  onVoiceClick?: () => void;
  isMobileView?: boolean;
  initialMessageIsVoice?: boolean;
  isProfileActive?: boolean;
  userProfile?: UserProfile | null;
  onConsultationResponse?: (response: string, ailmentId: string) => void;
  onResetChat?: () => void;
}

const NANI_VOICE_PROMPT = `
## Voice Identity: Nature Nani
Tone: Grandmotherly, warm, rhythmic.
Identity: Wise Ayurvedic guide.
`;


function shouldRenderManasAid(
  metadata: RecommendationMetadata,
  userQuery: string
): boolean {
  // If Orchestrator didn't include Manas Aid (WISDOM type), never show it
  if (metadata.type !== 'WISDOM') return true; // Other types are always allowed

  const queryLower = userQuery.toLowerCase();

  // Explicit emotional/mental health keywords — show Manas Aid
  const triggerKeywords = [
    'sad', 'sadness', 'depress', 'anxious', 'anxiety', 'hopeless',
    'worthless', 'empty', 'numb', 'grief', 'lonely', 'loneliness',
    'angry', 'rage', 'mood swing', 'mental health', 'panic',
    'trauma', 'no motivation', 'lost interest', "don't care",
    'pointless', 'purpose', 'spiritual', 'wisdom', 'vedic',
    'meaning of life', 'disconnected', 'lost faith'
  ];

  const hasTriggerKeyword = triggerKeywords.some(kw => queryLower.includes(kw));

  // Physical/dietary keywords that should block Manas Aid
  const blockingKeywords = [
    'cooling meal', 'meal plan', 'what to eat', 'diet', 'food',
    'pitta meal', 'vata meal', 'kapha meal', 'recipe', 'nutrition',
    'herb', 'botanical', 'yoga pose', 'stretch', 'exercise',
    'sleep better', 'digestion', 'inflammation', 'weight'
  ];

  const hasBlockingKeyword = blockingKeywords.some(kw => queryLower.includes(kw));

  // Block if dietary/physical context dominates with no emotional signal
  if (hasBlockingKeyword && !hasTriggerKeyword) {
    return false;
  }

  return hasTriggerKeyword;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  setMessages,
  onTrialEnd,
  hasAccess,
  subscriptionStatus,
  initialMessage,
  onMessageSent,
  usage,
  onUpgradeClick,
  isGuest,
  onShowAuth,
  onNavigateToFeature,
  onVoiceClick,
  initialMessageIsVoice = false,
  isProfileActive = false,
  userProfile = null,
  onConsultationResponse,
  onResetChat
}) => {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null);
  const [restoredMessage, setRestoredMessage] = useState(false);
  const [savingConsultationId, setSavingConsultationId] = useState<string | null>(null);
  const [savedConsultationIds, setSavedConsultationIds] = useState<Record<string, boolean>>({});
  const [sessionSavedAilments, setSessionSavedAilments] = useState<Set<string>>(new Set());

  const handleSaveConsultation = async (msg: Message) => {
    if (!hasAccess) { onUpgradeClick(); return; }
    const user = getCurrentUser();
    if (!user) { onShowAuth(); return; }

    setSavingConsultationId(msg.id);
    try {
      let ailmentName = "Holistic Consultation";
      if (msg.recommendations?.[0]?.id) {
        ailmentName = msg.recommendations[0].id.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      }

      if (sessionSavedAilments.has(ailmentName)) {
        if (!window.confirm(`You already have a saved consultation for ${ailmentName}. Replace it?`)) {
            setSavingConsultationId(null);
            return;
        }
      }

      const user_question = messages
        .filter(m => m.role === 'user' && m.timestamp < msg.timestamp)
        .slice(-1)[0]?.content || "Holistic Inquiry";

      const consultationData = {
          ailment_name: ailmentName,
          user_question: user_question,
          nani_response: msg.content,
          saved_at: new Date().toISOString(),
          // chat_history remains for transcript view
          chat_history: messages.map(m => ({ role: m.role, content: m.content }))
      };

      const result = await saveConsultation(user, consultationData, ailmentName);
      if (result) {
          setSavedConsultationIds(prev => ({ ...prev, [msg.id]: true }));
          setSessionSavedAilments(prev => new Set(prev).add(ailmentName));
          const toast = document.createElement('div');
          toast.className = 'fixed top-4 right-4 bg-[#2D6A4F] text-white px-6 py-4 rounded-xl shadow-2xl z-[9999] animate-in slide-in-from-top-5 font-medium flex items-center gap-3 border border-[#347A5A]';
          toast.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg> <span>Consultation saved to your Healing Library</span>';
          document.body.appendChild(toast);
          setTimeout(() => { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 4000);
      } else {
          const toast = document.createElement('div');
          toast.className = 'fixed top-4 right-4 bg-red-600 text-white px-6 py-4 rounded-xl shadow-2xl z-[9999] animate-in slide-in-from-top-5 font-medium flex items-center gap-3';
          toast.innerHTML = '<span>⚠️</span> Unable to save — please try again';
          document.body.appendChild(toast);
          setTimeout(() => { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 4000);
      }
    } catch (e) {
        console.error(e);
    } finally {
        setSavingConsultationId(null);
    }
  };

  useEffect(() => {
    const pendingMsg = sessionStorage.getItem('naturenani_pending_message');
    // We also check for 'nani_pending_message' to migrate from any previous versions
    const legacyMsg = sessionStorage.getItem('nani_pending_message');
    const msgToRestore = pendingMsg || legacyMsg;
    
    if (msgToRestore && !isGuest && getCurrentUser()) {
      setInput(msgToRestore);
      sessionStorage.removeItem('naturenani_pending_message');
      sessionStorage.removeItem('nani_pending_message');
      setRestoredMessage(true);
    }
  }, [isGuest]);
  const [isInputFocused, setIsInputFocused] = useState(false); // Track focus
  const [quoteIndex, setQuoteIndex] = useState(0); // Track rotating quote index
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const [runTour, setRunTour] = useState(false);

  // Check profile completeness
  const isProfileComplete = userProfile?.age && userProfile?.sex && userProfile?.health_data?.medical_history;

  const CONFIDENCE_QUOTES = [
    "Personalized to your age, health conditions, and body constitution",
    "Guided by wisdom from 200+ ancient Ayurvedic & naturopathic texts",
    "Natural remedies that adapt as your health journey evolves"
  ];

  useEffect(() => {
    // Start tour ONLY if messages are empty and not loading
    if (messages.length <= 1 && !isLoading) {
      // Small delay to ensure render
      setTimeout(() => setRunTour(true), 1000);
    }
  }, [messages.length, isLoading]);

  useEffect(() => {
    const interval = setInterval(() => {
      setQuoteIndex((prev) => (prev + 1) % CONFIDENCE_QUOTES.length);
    }, 10000); // 10 seconds
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (initialMessage && initialMessage.trim() !== '' && !isLoading) {
      handleAutoSend(initialMessage, false, initialMessageIsVoice);
    }
  }, [initialMessage, initialMessageIsVoice]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, isLoading, scrollToBottom]);

  const handleResetChat = useCallback(() => {
    if (isLoading) return;
    setMessages([{
      id: 'welcome',
      role: 'model',
      content: 'Namaste. I am Nature Nani. Tell me what ailments you are experiencing. To give you the best wisdom from our scrolls, tell me your age, sex and any past medical history or other health concerns I should know about',
      timestamp: Date.now()
    }]);
    if (onResetChat) onResetChat();
    if (onMessageSent) onMessageSent();
  }, [setMessages, isLoading, onMessageSent, onResetChat]);

  const generateAndPlaySpeech = async (msgId: string, fullText: string) => {
    if (!hasAccess) { onUpgradeClick(); return; }
    if (isSpeaking) return;
    setIsSpeaking(true);
    setPlayingMessageId(msgId);
    try {
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/consultation`;
      const response = await authenticatedFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "tts",
          prompt: `${NANI_VOICE_PROMPT}\n\nCONTENT:\n${fullText}`,
          config: {
            responseModalities: ["AUDIO"],
            speechConfig: { voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } } }
          }
        })
      });
      const data = await response.json();
      // Updated to match the new edge function response key
      const audioData = data.audio;
      if (audioData) {
        if (!audioContextRef.current) { audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 }); }
        await playRawAudio(audioData, audioContextRef.current);
      }
    } catch (e) { } finally { setIsSpeaking(false); setPlayingMessageId(null); }
  };

  const parseMessageContent = (rawText: string) => {
    let visibleText = rawText;
    let metadata: RecommendationMetadata[] = [];
    let suggestions: string[] = [];

    const marker = "[METADATA_START]";
    const markerIndex = visibleText.indexOf(marker);

    if (markerIndex !== -1) {
      const metadataPart = visibleText.substring(markerIndex + marker.length);
      visibleText = visibleText.substring(0, markerIndex).trim();

      const jsonCandidate = metadataPart.replace(/```json|```/g, '').trim();

      try {
        const data = JSON.parse(jsonCandidate);
        if (data.recommendations) metadata = data.recommendations;
        if (data.suggestions) suggestions = data.suggestions;
      } catch (e) {
        try {
          const recMatch = jsonCandidate.match(/"recommendations":\s*(\[[\s\S]*?\])/);
          if (recMatch) {
            const arrText = recMatch[1];
            metadata = JSON.parse(arrText + (arrText.endsWith(']') ? '' : ']'));
          }
          const sugMatch = jsonCandidate.match(/"suggestions":\s*(\[[\s\S]*?\])/);
          if (sugMatch) {
            const arrText = sugMatch[1];
            suggestions = JSON.parse(arrText + (arrText.endsWith(']') ? '' : ']'));
          }
        } catch (innerE) { }
      }
    }

    return { visibleText, metadata, suggestions };
  };

  const handleAutoSend = async (text: string, isResuming = false, isVoiceQuery = false) => {
    if (isLoading || !text.trim()) return;
    if (text === "New Consultation") { handleResetChat(); return; }

    const currentUser = getCurrentUser();
    if (isGuest || !currentUser) {
      sessionStorage.setItem('naturenani_pending_message', text);
      onShowAuth();
      return;
    }

    const isConsultationStart = messages.length <= 1;

    // Check usage limit before proceeding 
    if (!usage.isUnlimited) {
      if (usage.remaining <= 0) {
        if (currentUser.subscription_status === 'trialing') {
          setMessages(prev => [...prev, {
            id: crypto.randomUUID(),
            role: 'model',
            content: "Nani says: You've reached your daily limit for the trial! Take some time to reflect on the wisdom shared so far. You'll have 3 more consultations tomorrow, or upgrade now for unlimited access.",
            timestamp: Date.now()
          }]);
          return;
        } else {
          onUpgradeClick();
          return;
        }
      }
    }

    const userMessage: Message = { id: crypto.randomUUID(), role: 'user', content: text, timestamp: Date.now() };
    if (!isResuming) setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const botMessageId = crypto.randomUUID();
      let fullRawContent = '';
      setMessages(prev => [...prev, { id: botMessageId, role: 'model', content: '', timestamp: Date.now() }]);

      const stream = sendMessageWithRAG(text, messages, hasAccess ? 'Premium' : 'Free', usage.count, currentUser, undefined, false);

      for await (const chunk of stream) {
        fullRawContent += chunk;
        const { visibleText, metadata, suggestions } = parseMessageContent(fullRawContent);
        setMessages(prev => prev.map(msg => msg.id === botMessageId ? {
          ...msg,
          content: visibleText,
          recommendations: metadata.length > 0 ? metadata : msg.recommendations,
          suggestions: suggestions.length > 0 ? suggestions : msg.suggestions
        } : msg));
      }

      // Increment count for verified user if this was a counted query (i.e. not an intake question)
      const isIntakeQuestion = fullRawContent.toLowerCase().includes('tell me your age');

      if (currentUser && !isIntakeQuestion) {
        const { incrementQueryCount } = await import('../services/backendService');
        await incrementQueryCount(currentUser.id);
      }

      if (onMessageSent) {
        setTimeout(() => onMessageSent(), 500);
      }

      // Report consultation response to parent for module integration
      const { metadata } = parseMessageContent(fullRawContent);
      const remedyRec = metadata.find(m => m.type === 'REMEDY' || m.type === 'DIET' || m.type === 'YOGA');
      if (remedyRec && remedyRec.id && onConsultationResponse) {
        onConsultationResponse(fullRawContent, remedyRec.id);
      }
    } catch (error: any) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = () => { if (!input.trim() || isLoading) return; const text = input; setInput(''); handleAutoSend(text); };

  const renderMarkdown = (content: string) => (
    <div className="markdown-content prose prose-slate max-w-none text-left">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
        h3: ({ node, ...props }: any) => <h3 className="font-serif font-bold text-sage-800 text-lg mt-6 mb-3 border-b border-sage-50 pb-2" {...props} />,
        p: ({ node, ...props }: any) => <p className="mb-4 last:mb-0 leading-relaxed text-gray-700" {...props} />,
        table: ({ node, ...props }: any) => <div className="overflow-x-auto my-4 rounded-xl border border-sage-100 shadow-sm"><table className="min-w-full" {...props} /></div>,
        th: ({ node, ...props }: any) => <th className="px-3 py-3 text-left text-[10px] font-bold text-white uppercase tracking-wider bg-sage-600" {...props} />,
        td: ({ node, ...props }: any) => <td className="px-3 py-3 text-sm text-gray-700 border-b border-sage-50" {...props} />,
      }}>{content}</ReactMarkdown>
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-indigo-50/20 via-purple-50/20 to-blue-50/10 transition-colors duration-1000 relative">
      <WelcomeTour run={runTour} onClose={() => setRunTour(false)} />

      <div className="bg-white/80 backdrop-blur-md border-b border-sage-100 p-4 shadow-sm flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <Logo className="h-8 w-8" textClassName="text-lg" showSlogan={false} />
          {/* Dynamic Banner - Moved from footer */}
          <div className="hidden md:block border-l border-sage-200 pl-4 py-1 h-6 overflow-hidden min-w-[350px]">
            <span key={quoteIndex} className="block text-xs font-medium text-sage-600 tracking-wide animate-in fade-in slide-in-from-bottom-2 duration-700 whitespace-nowrap">
              {CONFIDENCE_QUOTES[quoteIndex]}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {!usage.isUnlimited && !isGuest && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-sage-50 border border-sage-100 rounded-full">
              <span className="text-[10px] font-black text-sage-700 uppercase">{usage.count} / {usage.limit} Daily</span>
            </div>
          )}

          <button onClick={handleResetChat} disabled={isLoading} className="p-2 text-sage-400 hover:text-sage-600 flex items-center gap-2 text-xs font-bold uppercase transition-colors"><RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /> Reset</button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-8 space-y-8 pb-48 scroll-smooth relative" id="chat-container">
        {/* Empty State: Wisdom Center */}
        {messages.length <= 1 && !isLoading && (
          <div className="min-h-[60vh] flex flex-col items-center justify-center transition-all duration-700 ease-in-out">
            <div className="w-full max-w-4xl flex flex-col items-center">

              {/* Conditional CTA for Incomplete Profile */}
              {!isProfileComplete && !isGuest && (
                <button
                  id="tour-health-story"
                  onClick={() => onNavigateToFeature(AppView.PROFILE, {} as FeatureContext)}
                  className="mb-8 group flex items-center gap-3 bg-sage-600 hover:bg-sage-700 text-white pl-4 pr-6 py-2.5 rounded-full shadow-lg shadow-sage-200 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 animate-in fade-in slide-in-from-top-4"
                >
                  <div className="bg-white/20 p-1.5 rounded-full">
                    <User size={16} />
                  </div>
                  <div className="text-left">
                    <p className="text-[10px] font-medium opacity-80 uppercase tracking-wider">Enhance your insights</p>
                    <p className="text-sm font-bold flex items-center gap-2">
                      Start Your Health Story <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </p>
                  </div>
                </button>
              )}

              <WisdomNugget isFocused={isInputFocused} />

              {/* Daily Rituals Section - Replaces StarterPrompts */}
              <div id="tour-healing-rituals" className="w-full">
                <DailyRituals userProfile={userProfile} onSelect={(text) => handleAutoSend(text)} />
              </div>
            </div>
          </div>
        )}

        {/* Existing Messages Loop */}
        {messages.slice(1).map((msg) => (
          <div key={msg.id} className="flex flex-col gap-2 max-w-5xl mx-auto w-full">
            {/* ... Message rendering logic */}
            <div className={`flex items-start gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
              <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center shadow-sm ${msg.role === 'user' ? 'bg-earth-500' : 'bg-gradient-to-br from-emerald-500 to-sage-600'}`}>
                {msg.role === 'user' ? <User size={16} className="text-white" /> : <Leaf size={16} className="text-white" />}
              </div>
              <div className={`max-w-[85%] relative rounded-3xl p-6 shadow-sm ${msg.role === 'user' ? 'bg-white text-sage-900 border border-sage-100' : 'bg-white text-gray-800 border border-sage-200/60'}`}>
                {msg.content ? renderMarkdown(msg.content) : (isLoading && msg.role === 'model' ? <div className="flex gap-2"><div className="w-2 h-2 bg-sage-400 rounded-full animate-bounce"></div><div className="w-2 h-2 bg-sage-400 rounded-full animate-bounce delay-100"></div><div className="w-2 h-2 bg-sage-400 rounded-full animate-bounce delay-200"></div></div> : null)}

                {msg.role === 'model' && msg.content && msg.recommendations && msg.recommendations.length > 0 && (
                  <div className="mt-6 p-4 rounded-xl bg-gradient-to-br from-indigo-50/50 to-purple-50/50 border border-indigo-100/50">
                    <h4 className="text-[11px] font-bold text-indigo-900 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                      <Sparkles size={14} className="text-indigo-500" />
                      What to Expect
                    </h4>
                    <p className="text-[13px] text-indigo-800 leading-relaxed font-medium">
                      Natural healing is a journey, not a quick fix. You may notice subtle shifts in your energy or digestion within a few days, but deeper patterns often take 3-4 weeks to rebalance. <strong className="text-indigo-950 font-black">Consistency with your chosen priority actions is key.</strong> If symptoms persist or worsen, please consult your healthcare provider.
                    </p>
                  </div>
                )}

                {msg.role === 'model' && msg.content && (
                  <div className="mt-4 pt-4 border-t border-sage-50 text-[10px] text-sage-400 font-medium opacity-80">
                    <p className="flex items-center gap-1.5 mb-1.5">
                      ℹ️ Educational guidance only • Not medical advice • Consult your healthcare provider
                    </p>
                    <p className="flex items-center gap-1.5 text-red-400">
                      🚨 Emergency? Contact your local emergency services
                    </p>
                  </div>
                )}

                {/* Soft Wisdom Nudge - Appears when WISDOM card is absent */}
                {msg.role === 'model' && msg.content && !msg.recommendations?.some(r => r.type === 'WISDOM') && (
                  <div className="mt-4 pt-4 border-t border-indigo-50/30">
                    <button 
                      onClick={() => onNavigateToFeature(AppView.WISDOM, { id: 'general', title: 'Daily Wisdom', detail: 'General spiritual grounding' } as FeatureContext)}
                      className="text-[12px] text-[#4C3B8A] hover:text-[#3B2D70] font-bold flex items-center gap-1.5 transition-colors group"
                    >
                      <span className="text-indigo-400 group-hover:rotate-12 transition-transform">✦</span>
                      For inner balance and spiritual wisdom, visit Manas Aid <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                    </button>
                  </div>
                )}

                {msg.role === 'model' && msg.content && (
                  <div className="absolute -bottom-3 -right-3 flex items-center gap-1 z-10">
                    {msg.recommendations && msg.recommendations.length > 0 && (
                      <button 
                        onClick={() => handleSaveConsultation(msg)}
                        disabled={savingConsultationId === msg.id || savedConsultationIds[msg.id]}
                        className={`group flex items-center gap-2 px-3.5 py-2 rounded-full shadow-lg border transition-all ${savedConsultationIds[msg.id] ? 'bg-[#2D6A4F] text-white border-[#2D6A4F]' : 'bg-white text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white border-[#2D6A4F]/20 hover:scale-105'}`}
                      >
                        {savingConsultationId === msg.id ? (
                          <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                        ) : savedConsultationIds[msg.id] ? (
                          <CheckCircle2 size={14} />
                        ) : (
                          <Bookmark size={14} className="group-hover:fill-current" />
                        )}
                        <span className="text-[10px] font-black uppercase tracking-tighter">{savedConsultationIds[msg.id] ? "Saved" : "Save Consultation"}</span>
                      </button>
                    )}
                    <button 
                      onClick={() => generateAndPlaySpeech(msg.id, msg.content)} 
                      className={`group flex items-center gap-2 px-3.5 py-2 rounded-full shadow-lg border transition-all ${playingMessageId === msg.id ? 'bg-sage-700 text-white animate-pulse' : 'bg-white text-sage-600 hover:scale-105'}`}
                    >
                      {playingMessageId === msg.id ? <Volume2 size={14} /> : (hasAccess ? <Volume2 size={14} /> : <Lock size={12} className="text-amber-500" />)}
                      <span className="text-[10px] font-black uppercase tracking-tighter">{hasAccess ? "Listen" : "Unlock Voice"}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {msg.recommendations && msg.recommendations.length > 0 && (
              <div className="ml-12 grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 animate-in fade-in slide-in-from-bottom-4">
                {msg.recommendations
                  .filter(rec => {
                    // Use findLast to get the MOST RECENT user message before this bot response,
                    // not the first — otherwise multi-turn chats always evaluate against query #1
                    const userQuery = [...messages].reverse().find(m => m.role === 'user' && m.timestamp < msg.timestamp)?.content || "";
                    const shouldRender = shouldRenderManasAid(rec, userQuery);
                    if (rec.type === 'WISDOM' && !shouldRender) {
                      logger.log(
                        `[ManasAid] Suppressed for query: "${userQuery.substring(0, 60)}..." — Orchestrator included it but client gate rejected (physical/dietary context)`
                      );
                    }
                    return shouldRender;
                  })
                  .map((rec, idx) => (
                  <div key={idx} className="bg-white rounded-2xl border border-sage-100 shadow-sm hover:shadow-md flex flex-col h-full overflow-hidden hover:ring-2 hover:ring-sage-200 transition-all">
                    <div className="p-5 flex-1">
                      <div className="flex items-center gap-2 mb-3">
                        <div className={`p-2 rounded-lg ${rec.type === 'YOGA' ? 'bg-pink-50 text-pink-500' : rec.type === 'DIET' ? 'bg-orange-50 text-orange-500' : rec.type === 'WISDOM' ? 'bg-indigo-50 text-indigo-500' : 'bg-blue-50 text-blue-500'}`}>
                          {rec.type === 'YOGA' ? <Flower2 size={16} /> : rec.type === 'DIET' ? <Utensils size={16} /> : rec.type === 'WISDOM' ? <Brain size={16} /> : <Stethoscope size={16} />}
                        </div>
                        <h4 className="font-serif font-bold text-sage-900 leading-tight text-sm">{rec.title}</h4>
                      </div>
                      <p className="text-xs text-gray-500 leading-relaxed mb-4">{rec.summary}</p>
                      <button
                        onClick={() => {
                          if (!hasAccess) { onUpgradeClick(); return; }
                          const view = rec.type === 'REMEDY' ? AppView.BOTANICAL : rec.type === 'YOGA' ? AppView.YOGA : rec.type === 'DIET' ? AppView.DIET : AppView.WISDOM;
                          onNavigateToFeature(view, {
                            id: rec.id,
                            title: rec.title,
                            detail: rec.detail,
                            sourceBook: rec.sourceBook
                          });
                        }}
                        className={`w-full py-2 rounded-lg font-bold text-[11px] uppercase tracking-wide text-white transition-all flex items-center justify-center gap-2 ${rec.type === 'YOGA' ? 'bg-pink-500 hover:bg-pink-600' : rec.type === 'DIET' ? 'bg-orange-500 hover:bg-orange-600' : rec.type === 'WISDOM' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-[#3B6EB1] hover:bg-[#2e568a]'}`}
                      >
                        {!hasAccess && <Lock size={10} />}
                        Details
                        <ChevronRight size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {msg.suggestions && msg.suggestions.length > 0 && (
              <div className="ml-12 mt-4 flex flex-wrap gap-2">
                {msg.suggestions.map((s, idx) => (
                  <button key={idx} onClick={() => handleAutoSend(s)} disabled={isLoading} className="bg-white border border-sage-200 px-4 py-2 rounded-full text-[13px] font-bold text-sage-700 hover:bg-sage-600 hover:text-white transition-all shadow-sm flex items-center gap-2">
                    <Sparkles size={12} className="text-yellow-500" />
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Consultation Hub for Tour */}
      <div id="tour-consultation-hub" className="p-4 bg-white/90 backdrop-blur-lg border-t border-sage-100 relative z-30 shadow-[0_-10px_40px_rgba(0,0,0,0.03)]">
        {/* Banner relocated to header */}

        <div className={`
             max-w-3xl mx-auto flex items-center gap-2 transition-all duration-300 rounded-[2rem] p-1.5
             ${isInputFocused ? 'shadow-[0_0_20px_rgba(16,185,129,0.2)] bg-white ring-1 ring-emerald-100' : 'bg-gray-50/50'}
        `}>
          <textarea
            value={input}
            onChange={(e) => {
              setInput(e.target.value.substring(0, MAX_PROMPT_LENGTH));
              if (restoredMessage) setRestoredMessage(false);
            }}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleSend())}
            placeholder={messages.length <= 1 ? "Tell me what's troubling you, and let's explore nature's remedies together..." : "Ask a follow-up, go deeper, or share a new concern..."}
            className={`
                w-full text-base md:text-lg bg-transparent border-0 rounded-3xl px-6 py-4 
                focus:outline-none focus:ring-0 resize-none h-[64px] transition-all duration-300 
                placeholder:text-gray-400 text-gray-800
            `}
          />
          <button onClick={onVoiceClick} className="p-3 text-sage-400 hover:text-sage-600 rounded-full hover:bg-sage-50 transition-all"><Mic size={22} /></button>
          <button
            onClick={handleSend}
            disabled={isLoading || !input.trim()}
            className={`
                    p-3.5 rounded-full shadow-lg transition-all active:scale-95 flex items-center justify-center
                    ${isLoading || !input.trim() ? 'bg-gray-300 text-gray-500' : 'bg-sage-600 text-white hover:bg-sage-700'}
            `}
          >
            {isLoading ? <div className="w-6 h-6 border-2 border-white/50 border-t-white rounded-full animate-spin"></div> : <Send size={20} className={input.trim() ? 'translate-x-0.5' : ''} />}
          </button>
        </div>

        {/* Restoration Message */}
        {restoredMessage && (
          <div className="mt-2 text-center text-xs text-emerald-600 font-medium animate-in fade-in slide-in-from-top-2">
            We saved your question — pick up right where you left off.
          </div>
        )}

        {/* Conditional Wellness Journey Link */}
        {!isGuest && (
          <div className="mt-3 text-center">
            <Link to="/onboarding" className="text-xs font-bold text-sage-600 hover:text-sage-800 flex items-center justify-center gap-1 transition-colors inline-flex">
              {isProfileActive ? 'Retake the Wellness Assessment' : 'Take the Wellness Assessment'} <ArrowRight size={12} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;
